import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { IdentityAdminService, IdentityRole, IdentityUser } from './identity-admin.service';
import { PlatformAdminService, TenantRecord } from '../platform/platform-admin.service';

@Component({
  standalone: true,
  selector: 'ain-identity-users',
  imports: [AinPageComponent, AinSearchAccordionComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, InputTextModule, ButtonModule, DialogModule, RouterLink],
  template: `
    <ain-page [eyebrow]="t('identity.eyebrow')" [title]="t('identity.usersPage.title')" [description]="t('identity.usersPage.description')">
      <div page-actions>
        <ain-info-hint
          titleKey="identity.usersPage.hint.title"
          descriptionKey="identity.usersPage.hint.description"
          [bulletKeys]="['identity.usersPage.hint.bullet1', 'identity.usersPage.hint.bullet2', 'identity.usersPage.hint.bullet3']" />
        <button pButton type="button" [label]="t('identity.usersPage.createUser')" (click)="createDialogVisible.set(true)"></button>
      </div>

      <ain-search-accordion [title]="t('platform.search.title')">
        <section class="search-grid">
          <input pInputText [(ngModel)]="filters.query" [placeholder]="t('platform.search.query')" />
          @if (isPlatformAdmin()) {
            <select [(ngModel)]="filters.tenantId">
              <option value="">{{ t('identity.usersPage.anyTenant') }}</option>
              @for (tenant of tenants(); track tenant.id) {
                <option [value]="tenant.id">{{ tenant.name }}</option>
              }
            </select>
          }
          <select [(ngModel)]="filters.status">
            <option value="">{{ t('identity.usersPage.anyStatus') }}</option>
            <option value="1">{{ t('identity.usersPage.status.active') }}</option>
            <option value="2">{{ t('identity.usersPage.status.suspended') }}</option>
            <option value="3">{{ t('identity.usersPage.status.deleted') }}</option>
          </select>
          <button pButton type="button" [label]="t('platform.search.apply')" (click)="reload()"></button>
          <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
        </section>
      </ain-search-accordion>

      @if (loading()) {
        <ain-state type="loading" [title]="t('identity.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('identity.unavailable')" [message]="error() ?? ''" />
      } @else {
        <p-table [value]="filteredUsers()">
          <ng-template pTemplate="header">
            <tr><th>{{ t('columns.displayName') }}</th><th>{{ t('columns.email') }}</th><th>{{ t('columns.tenant') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('identity.usersPage.actions') }}</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.displayName }}</td>
              <td>{{ row.email }}</td>
              <td>{{ tenantName(row.tenantId) }}</td>
              <td>{{ userStatus(row.status) }}</td>
              <td class="actions-cell">
                <button pButton type="button" size="small" text="true" [label]="t('identity.usersPage.assignRole')" (click)="openAssignRole(row.id, row.tenantId)"></button>
                <button pButton type="button" size="small" text="true" [label]="t('identity.usersPage.resetPassword')" (click)="openResetPassword(row)"></button>
                <button pButton type="button" size="small" text="true" [label]="t('identity.usersPage.reinvite')" (click)="reinviteUser(row.id)"></button>
                <button pButton type="button" size="small" text="true" [label]="t('identity.usersPage.deactivate')" (click)="deactivateUser(row.id)"></button>
                <button pButton type="button" size="small" severity="danger" text="true" [label]="t('identity.usersPage.remove')" (click)="removeUser(row.id)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }

      <p-dialog [header]="t('identity.usersPage.createUser')" [visible]="createDialogVisible()" [modal]="true" [style]="{ width: '48rem', maxWidth: '95vw' }" (visibleChange)="createDialogVisible.set($event)">
        <section class="create-grid">
          @if (isPlatformAdmin()) {
            <select [(ngModel)]="createModel.tenantId">
              <option value="">{{ t('common.select') }}</option>
              @for (tenant of tenants(); track tenant.id) {
                <option [value]="tenant.id">{{ tenant.name }}</option>
              }
            </select>
          }
          <input pInputText [(ngModel)]="createModel.email" [placeholder]="t('columns.email')" />
          <input pInputText [(ngModel)]="createModel.displayName" [placeholder]="t('columns.displayName')" />
          <button pButton type="button" [label]="t('identity.usersPage.inviteUser')" (click)="inviteUser()"></button>
        </section>
        @if (inviteResult()) {
          <p class="temp-password">{{ t('identity.usersPage.tempPassword') }}: <code>{{ inviteResult() }}</code></p>
        }
      </p-dialog>

      <p-dialog [header]="t('identity.usersPage.assignRole')" [visible]="assignDialogVisible()" [modal]="true" [style]="{ width: '42rem', maxWidth: '95vw' }" (visibleChange)="assignDialogVisible.set($event)">
        <section class="create-grid">
          <select [(ngModel)]="assignment.roleId">
            <option value="">{{ t('common.select') }}</option>
            @for (role of roles(); track role.id) {
              <option [value]="role.id">{{ role.name }}</option>
            }
          </select>
          @if (isPlatformAdmin()) {
            <select [(ngModel)]="assignment.tenantId">
              <option value="">{{ t('common.select') }}</option>
              @for (tenant of tenants(); track tenant.id) {
                <option [value]="tenant.id">{{ tenant.name }}</option>
              }
            </select>
          }
          <button pButton type="button" [label]="t('common.save')" (click)="assignRole()"></button>
        </section>
      </p-dialog>

      <p-dialog
        [header]="t('identity.usersPage.resetPasswordDialog.title')"
        [visible]="resetDialogVisible()"
        [modal]="true"
        [style]="{ width: '42rem', maxWidth: '95vw' }"
        (visibleChange)="onResetDialogVisibleChange($event)">
        @if (resetTarget()) {
          <ain-info-hint
            titleKey="identity.usersPage.resetPasswordDialog.hint.title"
            descriptionKey="identity.usersPage.resetPasswordDialog.hint.description"
            [bulletKeys]="[
              'identity.usersPage.resetPasswordDialog.hint.bullet1',
              'identity.usersPage.resetPasswordDialog.hint.bullet2',
              'identity.usersPage.resetPasswordDialog.hint.bullet3'
            ]" />
          <p class="reset-user">
            <strong>{{ resetTarget()!.displayName }}</strong>
            <span>{{ resetTarget()!.email }}</span>
          </p>
          @if (!resetResult()) {
            <label class="password-field">
              <span>{{ t('identity.usersPage.resetPasswordDialog.password') }}</span>
              <div class="password-row">
                <input
                  pInputText
                  [(ngModel)]="resetPasswordValue"
                  [placeholder]="t('identity.usersPage.resetPasswordDialog.passwordPlaceholder')"
                  autocomplete="new-password"
                  class="password-input" />
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  [label]="t('identity.usersPage.resetPasswordDialog.generateRandom')"
                  [disabled]="resetBusy()"
                  (click)="generateRandomPassword()"></button>
              </div>
            </label>
            <p class="reset-help">{{ t('identity.usersPage.resetPasswordDialog.emptyMeansRandom') }}</p>
            <div class="dialog-actions">
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('common.cancel')"
                [disabled]="resetBusy()"
                (click)="resetDialogVisible.set(false)"></button>
              <button
                pButton
                type="button"
                [label]="t('identity.usersPage.resetPasswordDialog.submit')"
                [loading]="resetBusy()"
                [disabled]="resetBusy()"
                (click)="submitResetPassword()"></button>
            </div>
          } @else {
            <label class="password-field">
              <span>{{ t('identity.usersPage.tempPassword') }}</span>
              <div class="password-row">
                <input pInputText readonly [value]="resetResult()!" class="password-input" />
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  [label]="passwordCopied() ? t('identity.usersPage.resetPasswordDialog.copyDone') : t('identity.usersPage.resetPasswordDialog.copy')"
                  (click)="copyResetPassword()"></button>
              </div>
            </label>
            <p class="warning">{{ t('identity.usersPage.resetPasswordDialog.passwordWarning') }}</p>
            <div class="dialog-actions">
              <button
                pButton
                type="button"
                [label]="t('common.close')"
                (click)="resetDialogVisible.set(false)"></button>
            </div>
          }
        }
      </p-dialog>

      <div class="links">
        <a [routerLink]="['/identity/roles']">{{ t('identity.usersPage.goToRoles') }}</a>
      </div>
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: 2fr 1.4fr 1fr auto auto; gap: 8px; align-items: center; }
    .create-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; align-items: center; }
    .search-grid select, .create-grid select { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; min-height: 40px; }
    .actions-cell { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
    .temp-password { margin-top: 10px; color: #7c2d12; }
    .reset-user { display: grid; gap: 4px; margin: 12px 0; }
    .reset-user span { color: #475569; word-break: break-word; }
    .password-field { display: grid; gap: 6px; margin-top: 8px; }
    .password-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; }
    .password-input { width: 100%; min-width: 0; }
    .reset-help { margin: 8px 0 0; color: #64748b; font-size: 0.9rem; }
    .warning { margin-top: 10px; color: #7c2d12; }
    .dialog-actions { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; margin-top: 16px; }
    .links { margin-top: 12px; }
    .links a { color: var(--ain-color-primary); text-decoration: none; font-weight: 600; }
    @media (max-width: 1100px) {
      .search-grid, .create-grid { grid-template-columns: 1fr; }
      .password-row { grid-template-columns: 1fr; }
    }
  `]
})
export class IdentityUsersComponent implements OnInit {
  private readonly api = inject(IdentityAdminService);
  private readonly platformApi = inject(PlatformAdminService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly localizationService = inject(LocalizationService);

  readonly users = signal<IdentityUser[]>([]);
  readonly roles = signal<IdentityRole[]>([]);
  readonly tenants = signal<TenantRecord[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly createDialogVisible = signal(false);
  readonly assignDialogVisible = signal(false);
  readonly resetDialogVisible = signal(false);
  readonly resetBusy = signal(false);
  readonly resetTarget = signal<IdentityUser | null>(null);
  readonly resetResult = signal<string | null>(null);
  readonly passwordCopied = signal(false);
  readonly inviteResult = signal<string | null>(null);
  readonly currentTenantId = signal<string>('');
  readonly filteredUsers = computed(() => {
    const query = this.filters.query.trim().toLowerCase();
    return this.users().filter(row =>
      !query || row.displayName.toLowerCase().includes(query) || row.email.toLowerCase().includes(query));
  });

  filters = { query: '', tenantId: '', status: '' };
  createModel = { tenantId: '', email: '', displayName: '' };
  assignment = { userId: '', roleId: '', tenantId: '' };
  resetPasswordValue = '';

  ngOnInit(): void {
    this.currentContextService.loadContext().subscribe({
      next: context => {
        const tenantId = context.tenantId?.trim() ?? '';
        this.currentTenantId.set(tenantId);
        if (!context.isPlatformAdmin && tenantId) {
          this.createModel.tenantId = tenantId;
          this.assignment.tenantId = tenantId;
        }
        this.reload();
      },
      error: () => this.reload()
    });
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    let completed = 0;
    const markLoaded = (): void => {
      completed += 1;
      if (completed >= 3) this.loading.set(false);
    };

    this.api.listUsers({
      tenantId: this.effectiveListTenantId(),
      query: this.filters.query.trim() || undefined,
      status: this.filters.status.trim() || undefined,
      skip: 0,
      take: 300
    }).subscribe({
      next: rows => {
        this.users.set(rows.items);
        markLoaded();
      },
      error: () => {
        this.error.set(this.t('identity.errorUsers'));
        this.loading.set(false);
      }
    });

    this.api.listRoles({
      tenantId: this.effectiveListTenantId(),
      skip: 0,
      take: 300
    }).subscribe({
      next: rows => {
        this.roles.set(rows.items);
        markLoaded();
      },
      error: () => {
        this.error.set(this.t('identity.errorRoles'));
        this.loading.set(false);
      }
    });

    if (this.isPlatformAdmin()) {
      this.platformApi.listTenants({ skip: 0, take: 300 }).subscribe({
        next: rows => {
          this.tenants.set(rows.items);
          markLoaded();
        },
        error: () => {
          this.error.set(this.t('platform.tenants.errorLoad'));
          this.loading.set(false);
        }
      });
    } else {
      const tenantId = this.currentTenantId().trim();
      if (tenantId) {
        this.createModel.tenantId = tenantId;
        this.assignment.tenantId = tenantId;
      }
      markLoaded();
    }
  }

  inviteUser(): void {
    const effectiveTenantId = this.isPlatformAdmin()
      ? this.createModel.tenantId.trim()
      : this.currentTenantId().trim();
    const payload = {
      tenantId: effectiveTenantId,
      organizationUnitId: null,
      email: this.createModel.email.trim(),
      displayName: this.createModel.displayName.trim()
    };
    if (!payload.tenantId || !payload.email || !payload.displayName) return;

    this.api.inviteUser(payload).subscribe({
      next: result => {
        this.inviteResult.set(result.temporaryPassword);
        this.filters.tenantId = result.tenantId;
        this.createModel.tenantId = result.tenantId;
        this.reload();
      },
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  openAssignRole(userId: string, tenantId: string): void {
    this.assignment.userId = userId;
    this.assignment.tenantId = this.isPlatformAdmin()
      ? (tenantId || this.createModel.tenantId)
      : this.currentTenantId().trim();
    this.assignment.roleId = '';
    this.assignDialogVisible.set(true);
    this.api.listRoles({ tenantId: this.assignment.tenantId, skip: 0, take: 300 }).subscribe({
      next: rows => this.roles.set(rows.items),
      error: () => this.error.set(this.t('identity.errorRoles'))
    });
  }

  assignRole(): void {
    const effectiveTenantId = this.isPlatformAdmin()
      ? this.assignment.tenantId.trim()
      : this.currentTenantId().trim();
    if (!this.assignment.userId || !this.assignment.roleId || !effectiveTenantId) return;
    this.api.assignRoleToUser(this.assignment.userId, {
      roleId: this.assignment.roleId,
      tenantId: effectiveTenantId,
      organizationUnitId: null,
      scopeType: 0
    }).subscribe({
      next: () => this.assignDialogVisible.set(false),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  clearFilters(): void {
    this.filters.query = '';
    this.filters.tenantId = '';
    this.filters.status = '';
    this.reload();
  }

  reinviteUser(userId: string): void {
    this.api.reinviteUser(userId).subscribe({
      next: result => {
        if (result.temporaryPassword) {
          const existing = this.users().find(item => item.id === userId);
          this.resetTarget.set(existing ?? {
            id: userId,
            tenantId: '',
            organizationUnitId: null,
            email: '',
            displayName: this.t('identity.usersPage.reinvite'),
            status: 1
          });
          this.resetPasswordValue = '';
          this.resetResult.set(result.temporaryPassword);
          this.passwordCopied.set(false);
          this.resetDialogVisible.set(true);
        }
        this.reload();
      },
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  openResetPassword(user: IdentityUser): void {
    this.resetTarget.set(user);
    this.resetPasswordValue = '';
    this.resetResult.set(null);
    this.passwordCopied.set(false);
    this.resetBusy.set(false);
    this.resetDialogVisible.set(true);
  }

  onResetDialogVisibleChange(visible: boolean): void {
    this.resetDialogVisible.set(visible);
    if (!visible) {
      this.resetBusy.set(false);
      this.resetTarget.set(null);
      this.resetResult.set(null);
      this.resetPasswordValue = '';
      this.passwordCopied.set(false);
    }
  }

  generateRandomPassword(): void {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    const bytes = new Uint8Array(14);
    crypto.getRandomValues(bytes);
    let token = '';
    for (const value of bytes) {
      token += alphabet[value % alphabet.length];
    }
    this.resetPasswordValue = `Tmp!${token}1a`;
    this.passwordCopied.set(false);
  }

  submitResetPassword(): void {
    const user = this.resetTarget();
    if (!user || this.resetBusy()) return;

    this.resetBusy.set(true);
    this.error.set(null);
    this.api.resetUserPassword(user.id, this.resetPasswordValue).subscribe({
      next: result => {
        this.resetResult.set(result.temporaryPassword ?? (this.resetPasswordValue.trim() || null));
        this.passwordCopied.set(false);
        this.resetBusy.set(false);
      },
      error: () => {
        this.resetBusy.set(false);
        this.error.set(this.t('identity.usersPage.resetPasswordDialog.failed'));
      }
    });
  }

  copyResetPassword(): void {
    const password = this.resetResult();
    if (!password) return;
    void navigator.clipboard.writeText(password).then(() => {
      this.passwordCopied.set(true);
    });
  }

  deactivateUser(userId: string): void {
    this.api.deactivateUser(userId).subscribe({
      next: () => this.reload(),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  removeUser(userId: string): void {
    this.api.removeUser(userId).subscribe({
      next: () => this.reload(),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  userStatus(value: number | string): string {
    if (typeof value === 'string') return value;
    const map: Record<number, string> = {
      0: this.t('identity.usersPage.status.pending'),
      1: this.t('identity.usersPage.status.active'),
      2: this.t('identity.usersPage.status.suspended'),
      3: this.t('identity.usersPage.status.deleted')
    };
    return map[value] ?? `${value}`;
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  tenantName(tenantId: string): string {
    return this.tenants().find(item => item.id === tenantId)?.name ?? tenantId;
  }

  isPlatformAdmin(): boolean {
    return this.currentContextService.context()?.isPlatformAdmin === true;
  }

  private effectiveListTenantId(): string | undefined {
    if (this.isPlatformAdmin()) {
      return this.filters.tenantId.trim() || undefined;
    }
    return this.currentTenantId().trim() || undefined;
  }
}
